import React, { useState, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import verificationService from '../../services/onboarding/verificationService';
import './PersonalInformation.css';
import { useOnboarding } from './OnboardingContext';
import { getDecryptedUrl } from 'lib/utils';
import { useSelector } from 'react-redux';

// Debounce function to prevent excessive API calls
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(/^[A-Za-z ]+$/, 'First Name can only contain letters.')
    .required('First Name is required'),
  lastName: Yup.string()
    .matches(/^[A-Za-z ]+$/, 'Last Name can only contain letters.')
    .required('Last Name is required'),
  signupMethod: Yup.string().required('Please select a signup method'),
  phone: Yup.string().when('signupMethod', {
    is: 'phone',
    then: Yup.string()
      // .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
      .required('Phone number is required'),
    otherwise: Yup.string().notRequired()
  }),
  otp: Yup.string().when('signupMethod', {
    is: 'phone',
    then: Yup.string()
      .matches(/^[0-9]{6}$/, 'OTP must be exactly 6 digits')
      .required('OTP is required'),
    otherwise: Yup.string().notRequired()
  }),
  email: Yup.string().when('signupMethod', {
    is: 'email',
    then: Yup.string()
      .required('Email is required')
      .email('Please enter a valid email address')
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Please enter a valid email address format'
      )
      .min(5, 'Email must be at least 5 characters long')
      .max(254, 'Email must not exceed 254 characters')
      .test('no-consecutive-dots', 'Email cannot contain consecutive dots', (value) => {
        if (!value) return true;
        return !value.includes('..');
      })
      .test('valid-domain', 'Please enter a valid email domain', (value) => {
        if (!value) return true;
        const domain = value.split('@')[1];
        if (!domain) return false;
        return domain.length >= 3 && domain.includes('.');
      })
      .test('no-special-start-end', 'Email cannot start or end with special characters', (value) => {
        if (!value) return true;
        const [localPart] = value.split('@');
        return !localPart.startsWith('.') && !localPart.endsWith('.');
      }),
    otherwise: Yup.string().notRequired()
  }),
  emailOtp: Yup.string().when('signupMethod', {
    is: 'email',
    then: Yup.string()
      .matches(/^[0-9]{6}$/, 'Email OTP must be exactly 6 digits')
      .required('Email OTP is required'),
    otherwise: Yup.string().notRequired()
  }),
});

const relaxedValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(/^[A-Za-z ]+$/, 'First Name can only contain letters.')
    .required('First Name is required'),
  lastName: Yup.string()
    .matches(/^[A-Za-z ]+$/, 'Last Name can only contain letters.')
    .required('Last Name is required'),
  signupMethod: Yup.string().required('Please select a signup method'),
  phone: Yup.string().when('signupMethod', {
    is: 'phone',
    then: Yup.string()
      .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
      .required('Phone number is required'),
    otherwise: Yup.string().notRequired()
  }),
  email: Yup.string().when('signupMethod', {
    is: 'email',
    then: Yup.string()
      .required('Email is required')
      .email('Please enter a valid email address')
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'Please enter a valid email address format'
      )
      .min(5, 'Email must be at least 5 characters long')
      .max(254, 'Email must not exceed 254 characters')
      .test('no-consecutive-dots', 'Email cannot contain consecutive dots', (value) => {
        if (!value) return true;
        return !value.includes('..');
      })
      .test('valid-domain', 'Please enter a valid email domain', (value) => {
        if (!value) return true;
        const domain = value.split('@')[1];
        if (!domain) return false;
        return domain.length >= 3 && domain.includes('.');
      })
      .test('no-special-start-end', 'Email cannot start or end with special characters', (value) => {
        if (!value) return true;
        const [localPart] = value.split('@');
        return !localPart.startsWith('.') && !localPart.endsWith('.');
      }),
    otherwise: Yup.string().notRequired()
  }),
});

const PersonalInformation = forwardRef(({ title, onComplete, onStepDataChange, setError, isNextButtonDisable, setIsNextButtonDisable /**/ }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [phoneLogId, setPhoneLogId] = useState(null);
  const [emailLogId, setEmailLogId] = useState(null);
  const [verifiedPhone, setVerifiedPhone] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const {
    onboardingData,
    updateSection,
    markStepComplete,
    markStepIncomplete,
    getStepData,
    isStepComplete,
    resetOnboarding
  } = useOnboarding();


  const user = useSelector((state) => state.user);
  const isCtSuperuser = user?.profile?.person_role?.some((item) => item?.role_code === 'CT Superuser');
  const isCtCustomerSuccess = user?.profile?.person_role?.some((item) => item?.role_code === 'CT Customer Success');

  const loc = getDecryptedUrl(window.location.href);
  const isCreateAccount = loc.includes('isCreateAccount=true') && (isCtSuperuser || isCtCustomerSuccess);

  // Get existing data for this step
  const existingData = getStepData(0); // Step 0 is personalInfo

  // Initialize verification states from existing data
  useEffect(() => {
    if (existingData.phoneVerified) {
      setPhoneVerified(true);
    }
    if (existingData.emailVerified) {
      setEmailVerified(true);
    }
    if (existingData.otpSent) {
      setOtpSent(true);
    }
    if (existingData.emailVerificationSent) {
      setEmailVerificationSent(true);
    }
    if (existingData.showOTP) {
      setShowOTP(true);
    }
    if (existingData.showEmailVerification) {
      setShowEmailVerification(true);
    }
    // if (existingData.verifiedPhone) {
    //   setVerifiedPhone(existingData.verifiedPhone);
    // }
    // if (existingData.verifiedEmail) {
    //   setVerifiedEmail(existingData.verifiedEmail);
    // }
  }, [existingData]);

  // Detect existing retained onboarding session in localStorage
  useEffect(() => {
    setIsNextButtonDisable(!isCreateAccount);

    try {
      const saved = localStorage.getItem('onboardingData');
      if (saved) {
        const data = JSON.parse(saved);
        const hasAny =
          (data?.personalInfo && Object.keys(data.personalInfo).length > 0) ||
          (data?.businessDetails && Object.keys(data.businessDetails).length > 0) ||
          (data?.address && Object.keys(data.address).length > 0) ||
          (data?.msme && Object.keys(data.msme).length > 0) ||
          (data?.bank && Object.keys(data.bank).length > 0);
        setHasActiveSession(Boolean(hasAny));
      }
    } catch (e) {
      setHasActiveSession(false);
    }
  }, []);

  const clearActiveSession = () => {
    try { resetOnboarding && resetOnboarding(); } catch (e) {}
    try { localStorage.removeItem('onboardingData'); } catch (e) {}
    setHasActiveSession(false);
  };

  // Debounced API calls
  const debouncedSendOTP = useCallback(
    debounce(async (phone) => {
      if (!phone || phone.length !== 10) return;

      try {
        setPhoneLoading(true);
        setValidationErrors(prev => ({ ...prev, otp: null }));

        console.log('Sending OTP for phone:', phone);

        const otpPayload = {
          user_name: phone,
          login_through_enum: 1, // 1 for phone
          isResend: otpSent,
          access_method_type_enum: 2,
          agree: true,
          country_code: 91 // 91 for phone
        };

        console.log('OTP payload:', otpPayload);

        const response = await verificationService.sendOTP(otpPayload);

        console.log('OTP sent response:', response);

        if (response && response.log_id && response.user_name) {
          setPhoneLogId(response.log_id);
          setOtpSent(true);
          setShowOTP(true);
          setValidationErrors(prev => ({ ...prev, otp: null }));

          // Save state to context
          updateSection('personalInfo', {
            phoneLogId: response.log_id,
            otpSent: true,
            showOTP: true
          });
        } else {
          let errorMessage = response?.message || 'Failed to send OTP';
          if (response && response.errors && typeof response.errors === 'object') {
            const firstError = Object.values(response.errors)[0];
            if (firstError) {
              errorMessage = firstError;
            }
          }
          setValidationErrors(prev => ({
            ...prev,
            otp: errorMessage
          }));
        }
      } catch (error) {
        console.error('Error sending OTP:', error);
        let errorMessage = error.message || 'Error sending OTP';
        if (error.errors && typeof error.errors === 'object') {
          const firstError = Object.values(error.errors)[0];
          if (firstError) {
            errorMessage = firstError;
          }
        }
        setValidationErrors(prev => ({
          ...prev,
          otp: errorMessage
        }));
      } finally {
        setPhoneLoading(false);
      }
    }, 200),
    [otpSent]
  );

  const debouncedSendEmailOTP = useCallback(
    debounce(async (email) => {
      if (!email) return;

      // Email format validation before API call
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: 'Please enter a valid email address format'
        }));
        return;
      }

      // Additional validation checks
      if (email.length < 5 || email.length > 254) {
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: email.length < 5 ? 'Email is too short' : email.length > 254 ? 'Email is too long' : 'Invalid email address length'
        }));
        return;
      }

      if (email.includes('..')) {
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: 'Email cannot contain consecutive dots'
        }));
        return;
      }

      const domain = email.split('@')[1];
      if (!domain || domain.length < 3 || !domain.includes('.')) {
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: 'Please enter a valid email domain'
        }));
        return;
      }

      const [localPart] = email.split('@');
      if (localPart.startsWith('.') || localPart.endsWith('.')) {
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: 'Email cannot start or end with special characters'
        }));
        return;
      }

      try {
        setEmailLoading(true);
        setValidationErrors(prev => ({ ...prev, emailOTP: null }));

        console.log('Sending Email OTP for email:', email);

        const emailOtpPayload = {
          user_name: email,
          login_through_enum: 2, // 2 for email
          isResend: emailVerificationSent,
          access_method_type_enum: 2,
          agree: true,
          country_code: 0 // 0 for email
        };

        console.log('Email OTP payload:', emailOtpPayload);

        const response = await verificationService.sendOTP(emailOtpPayload);

        console.log('Email OTP sent response:', response);

        if (response && response.log_id && response.user_name) {
          setEmailLogId(response.log_id);
          setEmailVerificationSent(true);
          setShowEmailVerification(true);
          setValidationErrors(prev => ({ ...prev, emailOTP: null }));

          // Save state to context
          updateSection('personalInfo', {
            emailLogId: response.log_id,
            emailVerificationSent: true,
            showEmailVerification: true
          });
        } else {
          let errorMessage = response?.message || 'Failed to send Email OTP';
          if (response && response.errors && typeof response.errors === 'object') {
            const firstError = Object.values(response.errors)[0];
            if (firstError) {
              errorMessage = firstError;
            }
          }
          setValidationErrors(prev => ({
            ...prev,
            emailOTP: errorMessage
          }));
        }
      } catch (error) {
        console.error('Error sending Email OTP:', error);
        let errorMessage = error.message || 'Error sending Email OTP';
        console.log('error', error);
        if (error.errors && typeof error.errors === 'object') {
          const firstError = Object.values(error.errors)[0];
          console.log('firstError', firstError);
          if (firstError) {
            errorMessage = firstError;
          }
        }
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: errorMessage
        }));
      } finally {
        setEmailLoading(false);
      }
    }, 200),
    [emailVerificationSent]
  );

  const handleVerifyOTP = async (phone, otp) => {
    if (!otp || otp.length !== 6) return;

    try {
      setOtpLoading(true);
      setValidationErrors(prev => ({ ...prev, otp: null }));

      const response = await verificationService.verifyOTP({
        user_name: phone,
        otp: otp,
        login_through_enum: 1, // Phone
        access_method_type_enum: 2,
        log_id: phoneLogId
      });

      console.log('OTP verification response:', response);

      if (response && response.log_id && response.user_name) {
        setPhoneVerified(true);
        setVerifiedPhone(phone); // Store the verified phone number
        setValidationErrors(prev => ({ ...prev, otp: null }));
        setIsNextButtonDisable(false);

        // Save verification state to context
        updateSection('personalInfo', {
          phoneVerified: true,
          verifiedPhone: phone
        });
      } else {
        // Handle field validation errors
        let errorMessage = 'Invalid OTP';
        if (response && response.data) {
          // Extract field-specific error message
          const fieldErrors = Object.values(response.data);
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors[0]; // Get the first field error
          }
        } else if (response && response.message) {
          errorMessage = response.message;
        }

        setValidationErrors(prev => ({
          ...prev,
          otp: errorMessage
        }));
      }
    } catch (error) {
      let errorMessage = error.message || 'Error verifying OTP';
      if (error.errors && typeof error.errors === 'object') {
        const firstError = Object.values(error.errors)[0];
        if (firstError) {
          errorMessage = firstError;
        }
      }
      setValidationErrors(prev => ({
        ...prev,
        otp: errorMessage
      }));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyEmail = async (email, otp) => {
    if (!otp || otp.length !== 6) return;

    try {
      setEmailOtpLoading(true);
      setValidationErrors(prev => ({ ...prev, emailOTP: null }));

      console.log('Verifying Email OTP for email:', email, 'OTP:', otp);

      const response = await verificationService.verifyOTP({
        user_name: email,
        otp: otp,
        login_through_enum: 2, // 2 for email
        access_method_type_enum: 2,
        log_id: emailLogId
      });

      console.log('Email OTP verification response:', response);

      if (response && response.log_id && response.user_name) {
        setEmailVerified(true);
        setVerifiedEmail(email); // Store the verified email address
        setValidationErrors(prev => ({ ...prev, emailOTP: null }));
        setIsNextButtonDisable(false);

        // Save verification state to context
        updateSection('personalInfo', {
          emailVerified: true,
          verifiedEmail: email
        });
      } else {
        // Handle field validation errors
        let errorMessage = 'Invalid Email OTP';
        if (response && response.data) {
          // Extract field-specific error message
          const fieldErrors = Object.values(response.data);
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors[0]; // Get the first field error
          }
        } else if (response && response.message) {
          errorMessage = response.message;
        }
        console.log('Error verifying Email OTP:', response);
        setValidationErrors(prev => ({
          ...prev,
          emailOTP: errorMessage
        }));
      }
    } catch (error) {
      console.error('Error verifying Email OTP:', error);
      let errorMessage = error.message || 'Error verifying Email OTP';
      if (error.errors && typeof error.errors === 'object') {
        const firstError = Object.values(error.errors)[0];
        if (firstError) {
          errorMessage = firstError;
        }
      }
      setValidationErrors(prev => ({
        ...prev,
        emailOTP: errorMessage
      }));
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    console.log('handleSubmit called with:', values);

    // Save complete data including verification states
    const completeData = {
      ...values,
      phoneVerified,
      emailVerified,
      otpSent,
      emailVerificationSent,
      showOTP,
      showEmailVerification,
      phoneLogId,
      emailLogId,
      verifiedPhone,
      verifiedEmail
    };

    updateSection('personalInfo', completeData);

    // Mark step as complete if validation passes
    if (values.firstName && values.lastName && values.signupMethod) {
      if (values.signupMethod === 'phone' && phoneVerified) {
        markStepComplete(0);
      } else if (values.signupMethod === 'email' && emailVerified) {
        markStepComplete(0);
      } else {
        markStepIncomplete(0);
      }
    }

    onComplete({
      personalInformation: completeData
    });
    setSubmitting(false);
  };

  return (
    <div className="step-form">
      <h2>{title}</h2>
      {hasActiveSession && (
        <div style={{
          background: '#fff3cd',
          // border: '1px solid #ffeeba',
          borderRadius: '4px',
          padding: '0.875rem 1rem',
          marginBottom: '1rem',
          color: '#856404',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
            <span>An application is already in progress. To start a new one, please </span>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); clearActiveSession(); setShowOTP(false); setShowEmailVerification(false); setPhoneVerified(false); setEmailVerified(false); setValidationErrors({}); setError(null); localStorage.removeItem('cancelledChequeImage') }}
              style={{ color: '#1b62ab', textDecoration: 'underline', fontWeight: 600 }}
            >
              click here.
            </a>
          </div>
        </div>
      )}
      <Formik
        initialValues={{
          firstName: existingData.firstName || '',
          lastName: existingData.lastName || '',
          signupMethod: existingData.signupMethod || 'phone',
          email: existingData.email || '',
          phone: existingData.phone || '',
          // otp: existingData.otp || '',
          // emailOtp: existingData.emailOtp || ''
        }}
        validationSchema={isCreateAccount ? relaxedValidationSchema : validationSchema}
        onSubmit={handleSubmit}
        innerRef={ref}
        enableReinitialize={true}
      >

        {({ values, isSubmitting, touched, errors, setFieldValue, submitForm }) => {
          useImperativeHandle(ref, () => ({ submitForm }));
          return (
            <Form>
              {/* First Name */}
              <div className="form-field">
                <label htmlFor="firstName">First Name<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                <Field
                  type="text"
                  name="firstName"
                  id="firstName"
                  className={`form-input ${touched.firstName && errors.firstName ? 'error' : ''}`}
                  placeholder="Enter your first name"
                />
                <ErrorMessage name="firstName" component="div" className="error-message" />
              </div>
              {/* Last Name */}
              <div className="form-field">
                <label htmlFor="lastName">Last Name<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                <Field
                  type="text"
                  name="lastName"
                  id="lastName"
                  className={`form-input ${touched.lastName && errors.lastName ? 'error' : ''}`}
                  placeholder="Enter your last name"
                />
                <ErrorMessage name="lastName" component="div" className="error-message" />
              </div>
              {/* Signup Method Radio Group */}
              <div className="form-field">
                <label>Sign up via<span style={{ color: '#e10000', fontWeight: 'bold' }}>*</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1rem 0' }}>
                  <label className="ncswitch">
                    <Field
                      type="checkbox"
                      name="signupMethod"
                      checked={values.signupMethod === 'email'}
                      onChange={e => {
                        setFieldValue('signupMethod', e.target.checked ? 'email' : 'phone');
                        if (e.target.checked) {
                          setShowOTP(false);
                          setShowEmailVerification(false);
                          setPhoneVerified(false);
                          setEmailVerified(false);
                          setFieldValue('phone', '');
                          setFieldValue('otp', '');
                          setValidationErrors({});
                        } else {
                          setShowOTP(false);
                          setShowEmailVerification(false);
                          setPhoneVerified(false);
                          setEmailVerified(false);
                          setFieldValue('email', '');
                          setFieldValue('emailOtp', '');
                          setValidationErrors({});
                        }
                        setIsNextButtonDisable(!(isCreateAccount || (e.target.checked && verifiedEmail) || (e.target.checked != true && verifiedPhone)));
                      }}
                    />
                    <span className="ncswitch-slider"></span>
                  </label>
                  <span style={{ fontWeight: 500, color: '#757d84' }}>{values.signupMethod === 'email' ? 'Email' : 'Phone'}</span>
                </div>
                <ErrorMessage name="signupMethod" component="div" className="error-message" />
              </div>
              {/* Conditionally show phone fields */}
              {values.signupMethod === 'phone' && (
                <>
                  <div className="form-field">
                    <label htmlFor="phone">Phone Number</label>
                    <div style={{ display: 'flex', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <span style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #ced4da',
                          padding: '12px 12px',
                          borderRadius: '4px 0 0 4px',
                          color: '#495057',
                          fontSize: '14px',
                          fontWeight: '500',
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          marginRight: '10px',
                        }}>
                          +91
                        </span>
                        <Field
                          type="tel"
                          name="phone"
                          id="phone"
                          className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                          placeholder="Enter your phone number"
                          disabled={phoneLoading}
                          style={{
                            borderRadius: '4px 0 0 4px',
                            flex: 1,
                            borderRight: isCreateAccount ? '1px solid #ced4da' : 'none',
                            height: '50px'
                          }}
                          maxLength="10"
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setFieldValue('phone', value);
                            setFieldValue('otp', '');
                            setIsNextButtonDisable(!isCreateAccount);
                            setPhoneVerified(isCreateAccount);
                            setShowOTP(false);
                          }}
                        />
                      </div>
                      {
                        !isCreateAccount && (
                          <button
                          type="button"
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            background: phoneLoading || !values.phone || errors.phone || values.phone.length !== 10 || (phoneVerified && values.phone === verifiedPhone) ? '#6c757d' : '#1b62ab',
                            color: '#fff',
                            border: `1px solid ${phoneLoading || !values.phone || errors.phone || values.phone.length !== 10 || (phoneVerified && values.phone === verifiedPhone) ? '#6c757d' : '#1b62ab'}`,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            padding: '0 16px',
                            cursor: (phoneLoading || !values.phone || errors.phone || values.phone.length !== 10 || (phoneVerified && values.phone === verifiedPhone)) ? 'not-allowed' : 'pointer',
                            opacity: (phoneLoading || !values.phone || errors.phone || values.phone.length !== 10 || (phoneVerified && values.phone === verifiedPhone)) ? 0.6 : 1,
                            height: '50px',
                            minWidth: '100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0px 0.375rem 0.375rem 0px',
                            gap: '8px',
                            pointerEvents: isCreateAccount ? 'none' : 'auto'
                          }}
                          disabled={phoneLoading || !values.phone || errors.phone || values.phone.length !== 10 || (phoneVerified && values.phone === verifiedPhone)}
                          onClick={() => debouncedSendOTP(values.phone)}
                        >
                         {
                            phoneLoading ? (
                              <>
                                <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                Sending...
                              </>
                            ) : phoneVerified && values.phone === verifiedPhone ? (
                            'Verified'
                          ) : (
                              'Send OTP'
                            )
                         }
                        </button>
                        )
                      }
                    </div>
                    <small className="form-text">Only Indian phone numbers are supported (+91)</small>
                  </div>
                  {(showOTP || phoneVerified) && !isCreateAccount && (
                    <div className="form-field">
                      <label htmlFor="otp">OTP</label>
                      <div style={{ position: 'relative' }}>
                        <Field
                          type="text"
                          name="otp"
                          id="otp"
                          className={`form-input ${touched.otp && errors.otp ? 'error' : ''}`}
                          placeholder="Enter 6-digit OTP"
                          disabled={otpLoading}
                          maxLength="6"
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setIsNextButtonDisable(true);
                            setFieldValue('otp', value);
                            if (value.length === 6) {
                              handleVerifyOTP(values.phone, value);
                            }
                          }}
                        />
                        {otpLoading && (
                          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                          </div>
                        )}
                      </div>
                      <ErrorMessage name="otp" component="div" className="error-message" />
                    </div>
                  )}
                   <ErrorMessage name="phone" component="div" className="error-message" />
                    {phoneVerified && values.phone === verifiedPhone && (
                      <div style={{ color: '#28a745', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                        ✅ Phone verified successfully
                      </div>
                    )}
                    {validationErrors.otp && (
                      <div className="error-message">{validationErrors.otp}</div>
                      )}
                </>
              )}
              {/* Conditionally show email fields */}
              {values.signupMethod === 'email' && (
                <>
                  <div className="form-field">
                    <label htmlFor="email">Email</label>
                    <div style={{ display: 'flex', width: '100%' }}>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
                        placeholder="Enter your email"
                        style={{
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          flex: 1,
                          borderRight: isCreateAccount ? '1px solid #ced4da' : 'none',
                        }}
                        disabled={emailLoading}
                        onChange={(e) => {
                          setFieldValue('email', e.target.value);
                          setFieldValue('emailOtp', '');
                          setIsNextButtonDisable(!isCreateAccount);
                          setEmailVerified(isCreateAccount);
                          setShowEmailVerification(false);
                        }}
                      />
                      {
                        !isCreateAccount && (
                          <button
                          type="button"
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            background: emailLoading || !values.email || errors.email || (emailVerified && values.email === verifiedEmail) ? '#6c757d' : '#1b62ab',
                            color: '#fff',
                            border: `1px solid ${emailLoading || !values.email || errors.email || (emailVerified && values.email === verifiedEmail) ? '#6c757d' : '#1b62ab'}`,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            padding: '0 16px',
                            cursor: (emailLoading || !values.email || errors.email || (emailVerified && values.email === verifiedEmail)) ? 'not-allowed' : 'pointer',
                            opacity: (emailLoading || !values.email || errors.email || (emailVerified && values.email === verifiedEmail)) ? 0.6 : 1,
                            height: '50px',
                            minWidth: '100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0px 0.375rem 0.375rem 0px',
                            gap: '8px',
                            pointerEvents: isCreateAccount ? 'none' : 'auto'
                          }}
                          disabled={emailLoading || !values.email || errors.email || (emailVerified && values.email === verifiedEmail)}
                          onClick={() => debouncedSendEmailOTP(values.email)}
                        >
                         {
                          // !isCreateAccount ? (
                            emailLoading ? (
                              <>
                                <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                Sending...
                              </>
                            ) : emailVerified && values.email === verifiedEmail ? (
                            'Verified'
                          ) : (
                              'Send OTP'
                            )
                          // ) : null
                         }
                        </button>
                        )
                      }
                    </div>
                    <ErrorMessage name="email" component="div" className="error-message" />
                    <small className="form-text">We'll never share your email with anyone else.</small>
                  </div>
                  {(showEmailVerification || emailVerified) && !isCreateAccount && (
                    <div className="form-field">
                      <label htmlFor="emailOtp">Email OTP</label>
                      <div style={{ position: 'relative' }}>
                        <Field
                          type="text"
                          name="emailOtp"
                          id="emailOtp"
                          className={`form-input ${touched.emailOtp && errors.emailOtp ? 'error' : ''}`}
                          placeholder="Enter 6-digit OTP"
                          disabled={emailOtpLoading}
                          maxLength="6"
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setFieldValue('emailOtp', value);
                            setIsNextButtonDisable(true);
                            if (value.length === 6) {
                              handleVerifyEmail(values.email, value);
                            }
                          }}
                        />
                        {emailOtpLoading && (
                          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                          </div>
                        )}
                        {emailVerified && values.email === verifiedEmail && (
                          <div style={{ color: '#28a745', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                            ✅ Email verified successfully
                          </div>
                        )}
                      </div>
                      <ErrorMessage name="emailOtp" component="div" className="error-message" />
                    </div>
                  )}
                  {validationErrors?.emailOTP && (
                    <div className="error-message">{validationErrors?.emailOTP}</div>
                  )}
                </>
              )}
            </Form>
          );
        }}
      </Formik>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

export default PersonalInformation;
